import { useState, useEffect, useCallback, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { usePdfRenderer } from '../../hooks/usePdfRenderer';

// Ensure PDF.js worker is configured (bundled locally, no CDN dependency)
if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
}
import { usePdfFields } from '../../hooks/usePdfFields';
import { PdfZoomControls } from './PdfControls';
import PdfFieldOverlay from './PdfFieldOverlay';

const draftButtonStyle = {
  padding: '0.5rem 1rem',
  fontSize: '0.95rem',
  backgroundColor: '#0d6efd',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: '600',
  height: '34px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};
import { formService } from '../../services/formService';
import { useModal } from '../../context/ModalContext';
import { pdfToViewportCoords } from '../../utils/fieldEditorUtils';
import { getApiBaseUrl } from '../../services/api';

const PdfFormViewer = ({ formId, pdfUrl, onSubmit, onSaveDraft, onSaveDraftBlocked, token = null, stickyTopOffset = 0, initialFieldValues = null, onDirtyChange = null, saveDraftLabel = 'Save as Draft', allowSubmit = true, requirePrimaryField = true }) => {
  const [fieldConfiguration, setFieldConfiguration] = useState(null);
  const [overlayFields, setOverlayFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allPagesData, setAllPagesData] = useState([]);
  const [visiblePage, setVisiblePage] = useState(1);
  const [activeErrorField, setActiveErrorField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [viewerWidth, setViewerWidth] = useState(null);
  const viewerRef = useRef(null);
  const canvasRefs = useRef([]);
  const pageWrapperRefs = useRef([]);
  const textLayerRefs = useRef([]);
  const fieldRefs = useRef({});
  const activeRenderTasksRef = useRef([]);
  const renderCycleIdRef = useRef(0);

  // Refs for tracking submit/save state (used by sendBeacon on page unload)
  const hasSubmittedRef = useRef(false);
  const hasSavedDraftRef = useRef(false);
  const beaconSentRef = useRef(false);

  // Measure the viewer container width to constrain the canvas scroll area
  useEffect(() => {
    const measure = () => {
      if (viewerRef.current) {
        setViewerWidth(viewerRef.current.clientWidth);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const {
    pdfDoc,
    pdfLibDoc,
    pdfBytes,
    totalPages,
    scale,
    displayPercent,
    containerRef,
    zoomIn,
    zoomOut,
    resetZoom,
    error: pdfError
  } = usePdfRenderer(pdfUrl);

  const {
    fieldValues,
    fieldErrors,
    updateField,
    validateFields,
    setFieldErrors,
    resetFields
  } = usePdfFields(fieldConfiguration);

  // Restore field values from initialFieldValues (server draft) or localStorage (auto-save)
  useEffect(() => {
    if (!fieldConfiguration) return;

    // Drafts carry every signature inside fieldValues under its real PDF field name
    // (a form can have several, e.g. Patient's and Claimant's signature). The legacy
    // single "signature" key only ever held the FIRST one, so it is applied only as a
    // fallback for older drafts and must not overwrite a real field that already has
    // a value restored from fieldValues.
    const signatureFieldNames = overlayFields
      .filter((f) => f.type === 'signature')
      .map((f) => f.name);
    const restoredSignatureFields = new Set(
      Object.keys(initialFieldValues || {}).filter((k) => signatureFieldNames.includes(k))
    );

    // Priority: initialFieldValues (from server) > localStorage (auto-save)
    if (initialFieldValues) {
      Object.entries(initialFieldValues).forEach(([key, value]) => {
        if (key === '__currentPage') {
          // Restore the page the patient was on when they left
          const page = parseInt(value, 10);
          if (!isNaN(page) && page >= 1) {
            setVisiblePage(page);
          }
        } else if (key === 'signature') {
          // Legacy drafts only: map onto the first signature field, and only when
          // fieldValues did not already restore it.
          const target = signatureFieldNames[0];
          if (target && !restoredSignatureFields.has(target)) {
            updateField(target, value);
          }
        } else {
          updateField(key, value);
        }
      });
    } else if (formId) {
      try {
        const saved = localStorage.getItem(`pdf-draft-${formId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          Object.entries(parsed).forEach(([key, value]) => {
            if (key === '__currentPage') {
              const page = parseInt(value, 10);
              if (!isNaN(page) && page >= 1) {
                setVisiblePage(page);
              }
            } else {
              updateField(key, value);
            }
          });
        }
      } catch (_) {
        // Ignore parse errors
      }
    }
  }, [initialFieldValues, fieldConfiguration, updateField, formId, overlayFields]);

  // Report "has unsaved input" upwards so the page can warn before navigating away.
  // Compared against the values the form was opened with (server draft / prefill),
  // so restoring a draft does not by itself count as an unsaved change.
  const baselineRef = useRef(null);
  useEffect(() => {
    if (!onDirtyChange) return;

    const meaningful = (obj) =>
      Object.entries(obj || {})
        .filter(([, v]) => String(v ?? '').trim() !== '')
        .sort(([a], [b]) => a.localeCompare(b));

    // First run after the restore effect establishes the baseline.
    if (baselineRef.current === null) {
      baselineRef.current = JSON.stringify(meaningful(fieldValues));
      onDirtyChange(false);
      return;
    }

    onDirtyChange(JSON.stringify(meaningful(fieldValues)) !== baselineRef.current);
  }, [fieldValues, onDirtyChange]);

  // Load field configuration
  useEffect(() => {
    if (!formId) return;

    const loadConfig = async () => {
      try {
        const config = await formService.getFieldConfig(formId);
        setFieldConfiguration(config);
      } catch (err) {
        console.error('Error loading field configuration:', err);
      }
    };

    loadConfig();
  }, [formId]);

  // Build all pages data and overlay fields
  const buildAllPagesData = useCallback(async () => {
    if (!pdfDoc) return;

    try {
      setLoading(true);
      const pagesData = [];
      const allFields = [];
      const fields = fieldConfiguration?.fields || [];

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.0 });

        pagesData.push({
          pageNum,
          page,
          viewport,
          width: viewport.width,
          height: viewport.height
        });

        // Build fields for this page (if any configured)
        const pageFields = fields
          .filter(f => (f.page || 1) === pageNum && f.isVisible !== false && f.bounds &&
                       (f.bounds.width > 0 || f.bounds.height > 0))
          .map(f => {
            const viewportCoords = pdfToViewportCoords(viewport, f.bounds);
            if (!viewportCoords) return null;

            // Skip fields that are off-screen (negative coords or beyond page bounds)
            if (viewportCoords.x < -5 || viewportCoords.y < -5 ||
                viewportCoords.x > viewport.width + 5 || viewportCoords.y > viewport.height + 5) {
              return null;
            }

            return {
              name: f.pdfFieldName,
              type: f.displayType || 'text',
              page: pageNum,
              x: viewportCoords.x,
              y: viewportCoords.y,
              width: viewportCoords.width,
              height: viewportCoords.height,
              label: f.displayLabel,
              groupName: f.groupName,
              radioValue: f.pdfFieldName,
              fontSize: f.fontSize ? `${f.fontSize}px` : undefined,
              required: f.required,
              visibilityDependsOn: f.visibilityDependsOn || null,
              visibilityDependsOnValue: f.visibilityDependsOnValue || null
            };
          })
          .filter(Boolean);

        allFields.push(...pageFields);
      }

      setAllPagesData(pagesData);
      setOverlayFields(allFields);
    } catch (err) {
      console.error('Error building pages data:', err);
    } finally {
      setLoading(false);
    }
  }, [pdfDoc, fieldConfiguration, totalPages]);

  // Rebuild pages data when configuration or PDF changes
  useEffect(() => {
    buildAllPagesData();
  }, [buildAllPagesData]);

  // Render only the current visible page when scale, pages data, or visiblePage changes
  useEffect(() => {
    if (!allPagesData.length || scale === null) return;

    const pageIndex = visiblePage - 1;
    const pageData = allPagesData[pageIndex];
    if (!pageData) return;

    for (const task of activeRenderTasksRef.current) {
      try {
        task.cancel();
      } catch (_) {
        // ignore
      }
    }
    activeRenderTasksRef.current = [];

    renderCycleIdRef.current += 1;
    const thisRenderCycleId = renderCycleIdRef.current;
    const dpr = Math.max(window.devicePixelRatio || 1, 2);

    const renderCurrentPage = async () => {
      if (thisRenderCycleId !== renderCycleIdRef.current) return;

      const { page, pageNum } = pageData;
      const canvas = canvasRefs.current[0]; // Only one canvas now
      if (!canvas) return;

      try {
        const displayViewport = page.getViewport({ scale });
        const renderViewport = page.getViewport({ scale: scale * dpr });

        const context = canvas.getContext('2d');
        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;
        canvas.style.width = `${displayViewport.width}px`;
        canvas.style.height = `${displayViewport.height}px`;

        const renderContext = {
          canvasContext: context,
          viewport: renderViewport,
          annotationMode: 0
        };

        const renderTask = page.render(renderContext);
        activeRenderTasksRef.current.push(renderTask);
        await renderTask.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error(`Error rendering page ${pageNum}:`, err);
        }
      }

      if (thisRenderCycleId === renderCycleIdRef.current) {
        activeRenderTasksRef.current = [];
      }
    };

    renderCurrentPage();

    return () => {
      renderCycleIdRef.current += 1;
      for (const task of activeRenderTasksRef.current) {
        try {
          task.cancel();
        } catch (_) {
          // ignore
        }
      }
      activeRenderTasksRef.current = [];
    };
  }, [allPagesData, scale, visiblePage]);

  // Navigation helpers
  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setVisiblePage(pageNum);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const goNext = () => {
    // Check this page's required fields before moving on, so the patient fixes
    // gaps as they go instead of only finding out at submit time.
    const validation = validateFields(true, visiblePage);
    if (!validation.valid) {
      const firstMissingFieldName = Object.keys(validation.errors)[0];
      if (firstMissingFieldName) {
        setActiveErrorField(firstMissingFieldName);
        setTimeout(() => setActiveErrorField(null), 5000);
        setTimeout(() => {
          if (fieldRefs.current[firstMissingFieldName]) {
            fieldRefs.current[firstMissingFieldName].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
      return;
    }

    // Auto-save field values + current page to localStorage when navigating to next page
    if (formId && fieldValues) {
      try {
        const saveData = { ...fieldValues, __currentPage: String(visiblePage + 1) };
        localStorage.setItem(`pdf-draft-${formId}`, JSON.stringify(saveData));
      } catch (_) {
        // Silently ignore localStorage errors
      }
    }
    goToPage(visiblePage + 1);
  };
  const goPrev = () => goToPage(visiblePage - 1);
  const isLastPage = visiblePage >= totalPages;
  const isFirstPage = visiblePage <= 1;

  const { showAlert } = useModal();

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    // Validate required fields and set errors
    const validation = validateFields(true);
    if (!validation.valid) {
      console.warn('[Submit] Validation failed. Missing fields:', validation.missingFields);
      
      // Find the first missing field name from the errors object
      const firstMissingFieldName = Object.keys(validation.errors)[0];
      
      if (firstMissingFieldName) {
        // Find which page this field is on and navigate there
        const errorField = overlayFields.find(f => f.name === firstMissingFieldName);
        if (errorField && errorField.page !== visiblePage) {
          setVisiblePage(errorField.page || 1);
        }

        // Show tooltip on the first error field
        setActiveErrorField(firstMissingFieldName);
        
        // Auto-hide tooltip after 5 seconds
        setTimeout(() => setActiveErrorField(null), 5000);
        
        // Scroll to field after page renders
        setTimeout(() => {
          if (fieldRefs.current[firstMissingFieldName]) {
            fieldRefs.current[firstMissingFieldName].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
      
      return;
    }

    // Clear any active error tooltip
    setActiveErrorField(null);

    // Call parent submit handler
    if (onSubmit) {
      try {
        setSubmitting(true);
        hasSubmittedRef.current = true;
        // Strip internal __currentPage field before submitting
        const { __currentPage: _cp, ...submitValues } = fieldValues;
        await onSubmit({
          formId,
          fieldValues: submitValues,
          pdfBytes
        });
        // Clear localStorage draft after successful submission
        try { localStorage.removeItem(`pdf-draft-${formId}`); } catch (_) {}
      } finally {
        setSubmitting(false);
      }
    }
  };


  const handleSaveDraft = async () => {
    if (!onSaveDraft || submitting || savingDraft) {
      return;
    }

    // Pre-fill mode saves nothing that needs to be found again later, and the
    // primary field is often exactly what the patient should fill in, so the
    // gate below is skipped entirely.
    if (!requirePrimaryField) {
      try {
        setSavingDraft(true);
        hasSavedDraftRef.current = true;
        await onSaveDraft({
          formId,
          fieldValues,
          pdfBytes,
          primaryValue: null,
        });
      } finally {
        setSavingDraft(false);
      }
      return;
    }

    // A draft is only useful if staff can find it again, so the form's primary
    // field (it names the row in My Submissions) must be filled in first.
    const primaryKey = fieldConfiguration?.primaryFieldName;
    if (!primaryKey) {
      await onSaveDraftBlocked?.({
        reason: 'not-configured',
        message:
          'This form has no Primary Field set, so a draft could not be identified later. ' +
          'Please ask an administrator to set one in Field Editor -> Primary Field.',
      });
      return;
    }

    const primaryValue = (fieldValues?.[primaryKey] ?? '').toString().trim();
    if (!primaryValue) {
      const primaryField = (fieldConfiguration.fields || []).find(
        (f) => f.pdfFieldName === primaryKey
      );

      // Mark the field before the dialog so it is already flagged behind it.
      setFieldErrors((prev) => ({ ...prev, [primaryKey]: 'Required' }));

      // Tell the user why nothing was saved. The inline flag alone was not enough:
      // the Save button sits in the toolbar and the field can be on another page or
      // below the fold, so pressing Save looked like it had simply done nothing.
      const primaryLabel = primaryField?.displayLabel?.trim() || primaryKey;
      await onSaveDraftBlocked?.({
        reason: 'required',
        fieldName: primaryKey,
        message:
          `Please fill in "${primaryLabel}" before saving a draft. ` +
          'It is the field used to identify your form later.',
      });

      // Highlight and scroll only after the dialog closes. Running them first would
      // spend the highlight's five seconds behind the modal, and scroll a field the
      // user cannot see yet.
      if (primaryField?.page && primaryField.page !== visiblePage) {
        goToPage(primaryField.page);
      }
      setActiveErrorField(primaryKey);
      setTimeout(() => setActiveErrorField(null), 5000);
      setTimeout(() => {
        fieldRefs.current[primaryKey]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    // Clear any previous "Required" flag once the field is filled.
    setFieldErrors((prev) => {
      if (!prev?.[primaryKey]) return prev;
      const next = { ...prev };
      delete next[primaryKey];
      return next;
    });


    try {
      setSavingDraft(true);
      hasSavedDraftRef.current = true;
      // Include current page so patient can resume from where they left off
      const draftValues = { ...fieldValues, __currentPage: String(visiblePage) };
      await onSaveDraft({
        formId,
        fieldValues: draftValues,
        pdfBytes,
        primaryValue,
      });
      // Clear localStorage draft after successful server save
      try { localStorage.removeItem(`pdf-draft-${formId}`); } catch (_) {}
    } finally {
      setSavingDraft(false);
    }
  };


  // Keep refs up to date for sendBeacon closure (avoids stale closure problem)
  const latestFieldValuesRef = useRef(fieldValues);
  const latestVisiblePageRef = useRef(visiblePage);
  latestFieldValuesRef.current = fieldValues;
  latestVisiblePageRef.current = visiblePage;

  // Auto-save draft to server via sendBeacon when user leaves the page
  // This only saves data silently (no email). The backend DraftReminderService
  // will check for abandoned drafts and send resume emails after a delay.
  useEffect(() => {
    // Only activate for link-based forms (token must exist)
    if (!token || !formId) return;

    const sendBeaconDraft = () => {
      const currentValues = latestFieldValuesRef.current;
      const currentPage = latestVisiblePageRef.current;
      if (beaconSentRef.current || hasSubmittedRef.current) return;
      if (!currentValues || Object.keys(currentValues).length === 0) return;

      beaconSentRef.current = true;

      // Keep EVERY signature inside fieldValues, each under its real PDF field name.
      // A form can have more than one (e.g. patient + claimant). The previous version
      // stripped them all out here and kept only the last one in signatureDataUrl,
      // so this autosave silently erased the signatures a patient had already drawn.
      // Include __currentPage so the patient resumes where they left off.
      const cleanedValues = { __currentPage: String(currentPage), ...currentValues };

      // Still send the first signature separately for backward compatibility with
      // the server's single DraftSignatureDataUrl column.
      const firstSignature = Object.values(currentValues).find(
        (v) => typeof v === 'string' && v.startsWith('data:image/')
      ) || null;

      const payload = JSON.stringify({
        token,
        formId,
        fieldValues: cleanedValues,
        patientName: null,
        signatureDataUrl: firstSignature
      });

      // fetch(keepalive) instead of sendBeacon: sendBeacon caps the body at ~64KB,
      // which signature images blow past. keepalive survives page unload the same way.
      const apiBase = getApiBaseUrl();
      try {
        fetch(`${apiBase}/formlinks/save-draft-silent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          credentials: 'include',
          keepalive: true
        }).catch(() => {});
      } catch (_) {
        // Last-resort fallback; may be dropped if the payload is too large.
        navigator.sendBeacon(
          `${apiBase}/formlinks/save-draft-silent`,
          new Blob([payload], { type: 'application/json' })
        );
      }
    };

    const handleBeforeUnload = () => sendBeaconDraft();

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [token, formId]);

  if (pdfError) {
    return (
      <div className="pdf-error" style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Error loading PDF: {pdfError}</p>
      </div>
    );
  }

  if (!pdfDoc) {
    return (
      <div className="pdf-loading" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading PDF...</p>
      </div>
    );
  }

  return (
    <div ref={viewerRef} className="pdf-form-viewer" style={{
      width: '100%',
      margin: '0 auto',
      padding: '0',
      boxSizing: 'border-box'
    }}>
      {/* Fixed page/zoom controls bar */}
      <div style={{
        position: 'fixed',
        top: stickyTopOffset,
        left: 0,
        right: 0,
        zIndex: 19,
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        borderBottom: '1px solid #ddd'
      }}>
        <PdfZoomControls
          currentPage={visiblePage}
          totalPages={totalPages}
          displayPercent={displayPercent}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
          stickyTopOffset={stickyTopOffset}
          leftActions={onSaveDraft ? (
            <button
              onClick={handleSaveDraft}
              disabled={submitting || savingDraft}
              style={{
                ...draftButtonStyle,
                backgroundColor: savingDraft ? '#9ca3af' : draftButtonStyle.backgroundColor,
                cursor: (submitting || savingDraft) ? 'not-allowed' : 'pointer'
              }}
            >
              {savingDraft ? 'Saving...' : saveDraftLabel}
            </button>
          ) : null}
        />
      </div>

      {/* PDF canvas area - single page view */}
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          backgroundColor: '#e8e8e8',
          // Bottom padding clears the fixed submit bar. Without it the last field on a
          // page sits underneath the bar and cannot be clicked.
          padding: `calc(${stickyTopOffset}px + 56px) 0 96px`,
          boxSizing: 'border-box'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          width: 'max-content',
          minWidth: '100%',
          padding: '0 clamp(12px, 4vw, 32px)',
          boxSizing: 'border-box'
        }}>
          {allPagesData.length > 0 && (() => {
            const pageData = allPagesData[visiblePage - 1];
            if (!pageData) return null;
            const pageFields = overlayFields.filter(f => (f.page || 1) === pageData.pageNum);

            return (
              <div
                key={pageData.pageNum}
                data-page-num={pageData.pageNum}
                ref={el => pageWrapperRefs.current[0] = el}
                style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}
              >
                <canvas
                  ref={el => canvasRefs.current[0] = el}
                  style={{ display: 'block', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                />

                <PdfFieldOverlay
                  fields={pageFields}
                  fieldValues={fieldValues}
                  fieldErrors={fieldErrors}
                  onFieldChange={updateField}
                  fieldConfiguration={fieldConfiguration}
                  currentPage={pageData.pageNum}
                  scale={scale}
                  fieldRefs={fieldRefs}
                  activeErrorField={activeErrorField}
                />
              </div>
            );
          })()}
        </div>
      </div>

      {/* Navigation + Submit bar, pinned to the bottom of the viewport.
          Previously this sat at the end of the document flow, so on a short form the
          patient had to scroll past a page of blank space to find Submit, and on a long
          one they lost the page controls entirely. Fixed keeps both reachable at any
          scroll position; the canvas area below reserves matching padding so the last
          field is never hidden behind it. */}
      <div className="submit-section" style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        // Below the top toolbar's 19 so a dropdown opening upward from the bar does not
        // paint over the header.
        zIndex: 18,
        padding: '1rem',
        backgroundColor: '#f5f5f5',
        borderTop: '1px solid #ddd',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
        boxSizing: 'border-box'
      }}>
        {!isFirstPage && (
          <button
            onClick={goPrev}
            disabled={submitting || savingDraft}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (submitting || savingDraft) ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            ← Previous
          </button>
        )}

        {/* When submitting is disabled (pre-fill mode) the last page simply has no
            Submit button - staff finish with "Save & Return" in the toolbar. */}
        {!isLastPage ? (
          <button
            onClick={goNext}
            disabled={submitting || savingDraft}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              backgroundColor: 'var(--company-primary-color, #0d8a8a)',
              color: 'var(--company-primary-contrast, white)',
              border: 'none',
              borderRadius: '8px',
              cursor: (submitting || savingDraft) ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Next →
          </button>
        ) : allowSubmit ? (
          <button
            onClick={handleSubmit}
            className="btn-primary"
            disabled={submitting || savingDraft}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              backgroundColor: submitting ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (submitting || savingDraft) ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Form'}
          </button>
        ) : null}
      </div>

    </div>
  );
};

export default PdfFormViewer;
